/**
 * Fleet fuel efficiency trend chart for the Overview page (PR 2 of
 * dashboard standards).
 *
 * Recharts LineChart with two series:
 *   1. Actual L/100km — weighted average per time bucket
 *      (SUM(litres) / SUM(km_delta) * 100), so a bucket dominated by a
 *      couple of high-mileage assets isn't misrepresented by an
 *      arithmetic per-asset mean.
 *   2. Baseline — fleet-wide average of `ml.vehicle_baseline.baseline_
 *      l_per_100km_mean`. Constant across buckets, plotted as a flat
 *      reference line.
 *
 * Lower L/100km is better, so a *downward* actual line crossing below
 * the baseline is "good". Tooltip shows both values + the sample size.
 */

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { EfficiencyTrendResponse } from '@/types/overview';

const FLOAT2 = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 });

export interface EfficiencyTrendChartProps {
  data: EfficiencyTrendResponse | undefined;
  isLoading: boolean;
  error: Error | null;
}

interface ChartRow {
  bucket_label: string;
  actual: number | null;
  baseline: number | null;
  sample_n: number;
}

export function EfficiencyTrendChart({
  data,
  isLoading,
  error,
}: EfficiencyTrendChartProps): JSX.Element {
  const rows: ChartRow[] = (data?.buckets ?? []).map((b) => ({
    bucket_label: b.bucket_label,
    actual: b.actual_l_per_100km.value,
    baseline: b.baseline_l_per_100km,
    sample_n: b.actual_l_per_100km.sample_n,
  }));

  const baseline = data?.buckets?.[0]?.baseline_l_per_100km ?? null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Fuel Efficiency Trend (L/100km)</CardTitle>
      </CardHeader>
      <CardContent className="h-[320px] px-2 py-2">
        {error ? (
          <div className="flex h-full items-center justify-center text-sm text-destructive">
            Failed to load efficiency trend: {error.message}
          </div>
        ) : isLoading || !data ? (
          <Skeleton className="h-full w-full" />
        ) : rows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Not enough odometer pairs to compute fleet efficiency in this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="bucket_label" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                width={56}
                tickFormatter={(v: number) => FLOAT2.format(v)}
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{
                  background: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  fontSize: 12,
                }}
                formatter={(value, name) => {
                  const num = typeof value === 'number' ? value : null;
                  const seriesLabel = name === 'actual' ? 'Actual' : 'Baseline';
                  if (num == null) return ['—', seriesLabel];
                  return [`${FLOAT2.format(num)} L/100km`, seriesLabel];
                }}
                labelFormatter={(l, payload) => {
                  const n =
                    (payload?.[0]?.payload as ChartRow | undefined)?.sample_n ?? 0;
                  return `${l} · n=${n.toLocaleString('en-ZA')} fills`;
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                formatter={(v) => (v === 'actual' ? 'Actual (weighted)' : 'Baseline (fleet avg)')}
              />
              {baseline != null && (
                <ReferenceLine
                  y={baseline}
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                />
              )}
              <Line
                type="monotone"
                dataKey="actual"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="baseline"
                stroke="#94a3b8"
                strokeWidth={1}
                strokeDasharray="4 4"
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

export default EfficiencyTrendChart;
